import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { DefaultValues, FormProvider, useForm } from "react-hook-form";
import {
  PaziaButton,
  PaziaSelect,
  PaziaInput,
  PaziaFileDropzone,
  PaziaFormButton,
  IDropdownOption,
} from "components/atoms";
import { COURSE_FINANCE_OPTIONS, COURSE_FINANCE_TYPE } from "consts/courseFinance";
import { CourseFinanceApi, CreateCourseFinanseRequest } from "api/course-finances/courseFinancesApi";

interface CourseFinanceFormProps {
  onSubmit: (data: CreateCourseFinanseRequest) => Promise<void>;
  categories: IDropdownOption[];
  coursesOptions?: IDropdownOption[];
  finance?: CourseFinanceApi;
  courseId: number;
  onCancel?: () => void;
}
export const CourseFinanceForm = ({
  onSubmit,
  finance,
  categories,
  courseId,
  coursesOptions,
  onCancel,
}: CourseFinanceFormProps) => {
  const methods = useForm<CourseFinanceFormValues>({
    resolver: yupResolver(schema),
    defaultValues: toCourseFinanceFormDefaults(finance),
  });
  const { handleSubmit, reset } = methods;

  const onSubmitHandler = async (data: CourseFinanceFormValues) => {
    await onSubmit(toCourseFinanceRequest(data, courseId, !!finance?.invoice));
    reset(undefined, { keepDirty: false });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitHandler)} className="max-w-xl grid-cols-3 gap-2 md:grid">
        {coursesOptions && (
          <PaziaSelect
            name={COURSE_FINANCE_FORM_NAMES.COURSEID}
            options={coursesOptions}
            label="Kurs"
            className="col-span-3"
          />
        )}
        <div className="col-span-3">
          <PaziaInput name={COURSE_FINANCE_FORM_NAMES.NAME} label="Nazwa" type="text" required />
        </div>

        <PaziaSelect
          isClearable={false}
          name={COURSE_FINANCE_FORM_NAMES.CATEGORY_ID}
          options={categories}
          required
          label="Kategoria"
        />
        <PaziaSelect
          isClearable={false}
          name={COURSE_FINANCE_FORM_NAMES.TYPE}
          required
          options={COURSE_FINANCE_OPTIONS}
          label="Typ"
        />
        <PaziaInput name={COURSE_FINANCE_FORM_NAMES.VALUE} type="number" step="0.01" label="Wartość" required />
        <div className="col-span-3">
          <PaziaFileDropzone name={COURSE_FINANCE_FORM_NAMES.INVOICE} type={["image", "pdf"]} label="Faktura" />
        </div>

        <div className="col-span-3 flex justify-end gap-2 pt-4">
          {onCancel && <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCancel} />}
          <PaziaFormButton title={finance ? "Zapisz" : "Dodaj"} type="success" />
        </div>
      </form>
    </FormProvider>
  );
};

const COURSE_FINANCE_FORM_NAMES = {
  ID: "id",
  COURSEID: "courseId",
  NAME: "name",
  CATEGORY_ID: "categoryId",
  TYPE: "type",
  VALUE: "value",
  INVOICE: "invoice",
} as const;
const schema = yup
  .object({
    [COURSE_FINANCE_FORM_NAMES.ID]: yup.number(),
    [COURSE_FINANCE_FORM_NAMES.COURSEID]: yup.number(),
    [COURSE_FINANCE_FORM_NAMES.NAME]: yup.string().required(),
    [COURSE_FINANCE_FORM_NAMES.CATEGORY_ID]: yup.number().required(),
    [COURSE_FINANCE_FORM_NAMES.TYPE]: yup.number().required(),
    [COURSE_FINANCE_FORM_NAMES.VALUE]: yup.number().required(),
    // the API returns invoice: null when there is no file; yup v1 rejects null unless nullable
    [COURSE_FINANCE_FORM_NAMES.INVOICE]: yup.mixed().nullable(),
  })
  .required();
export type CourseFinanceFormValues = yup.InferType<typeof schema>;
export const courseFinanceFormSchema = schema;

// Expenses are stored as negative values (see onSubmitHandler), so the form shows the absolute value plus the type
export const toCourseFinanceFormDefaults = (finance?: CourseFinanceApi): DefaultValues<CourseFinanceFormValues> => ({
  [COURSE_FINANCE_FORM_NAMES.ID]: finance?.id,
  [COURSE_FINANCE_FORM_NAMES.NAME]: finance?.name,
  [COURSE_FINANCE_FORM_NAMES.CATEGORY_ID]: finance?.category?.id,
  [COURSE_FINANCE_FORM_NAMES.COURSEID]: finance?.courseId,
  [COURSE_FINANCE_FORM_NAMES.TYPE]:
    finance?.value == null ? undefined : finance.value < 0 ? COURSE_FINANCE_TYPE.EXPENSE : COURSE_FINANCE_TYPE.INCOME,
  [COURSE_FINANCE_FORM_NAMES.VALUE]: finance?.value == null ? undefined : Math.abs(finance.value),
  [COURSE_FINANCE_FORM_NAMES.INVOICE]: finance?.invoice,
});

// invoice field holds the stored file name (kept), dropped File[] (replace) or nothing (cleared via "Wyczyść")
export const toCourseFinanceRequest = (
  data: CourseFinanceFormValues,
  courseId: number,
  hadInvoice: boolean,
): CreateCourseFinanseRequest => {
  const { categoryId, type, value, invoice: dataInvoice, ...request } = data;
  const calculatedValue = type === COURSE_FINANCE_TYPE.EXPENSE ? -1 * value : value;
  return {
    ...request,
    courseFinanceCategoryId: categoryId,
    courseId: Number(courseId),
    invoice: Array.isArray(dataInvoice) && dataInvoice.length > 0 ? (dataInvoice[0] as File) : undefined,
    clearInvoice: hadInvoice && !dataInvoice,
    date: new Date(),
    value: calculatedValue,
  };
};
